import { FilterOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Card,
  Grid,
  Typography,
  Space,
  Button,
  Popconfirm,
  Table,
  Form,
  Badge,
  Popover,
  Select,
} from 'antd';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import { ColumnsType } from 'antd/lib/table';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Container from '../../../components/Container';
import FourZeroThree from '../../../components/FourZeroThree';
import TableFooterToolbar from '../../../components/table/TableFooterToolbar';
import TableToolbar from '../../../components/table/TableToolbar';
import {
  DEFAULT_FONT_SIZE,
  DEFAULT_SIZE_TYPE,
  GENERAL_TIMEOUT,
} from '../../../constants/systemConstants';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { FontSizeType, SMSTempData, SMSTempEnum } from '../../../types';
import { alertMessage } from '../../../utils/alertMessage';
import {
  getDataWithAuthToken,
  postDataWithAuthToken,
} from '../../../utils/axiosRequest';
import { compare, setFont } from '../../../utils/colComponents';
import { useVT } from 'virtualizedtableforantd4';
import SMSTemplateModal from '../../../components/plugins/SMSTemplateModal';
import FiveHundred from '../../../components/FiveHundred';
import { actionPermissions } from '../../../constants/actionPermissions';
import { hasPermission } from '../../../utils/hasPermission';
import { tableScrollToTop } from '../../../utils/helperFunction';

const SMSTemplateList = () => {
  //General Component
  const formRef = useRef(null);
  const isSubscribed = useRef(true);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [fourZeroThree, setFourZeroThree] = useState(false);
  const [fiveHundred, setFiveHundred] = useState(false);
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const [advance, setAdvance] = useState<{
    [key: string]: any;
  }>({});
  //Table Component
  const [tableSize, setTableSize] = useState<SizeType>(DEFAULT_SIZE_TYPE);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useLocalStorage('pageSize', '10');
  const [keyword, setKeyword] = useState('');
  const [vt] = useVT(() => ({ scroll: { y: 600 } }), []);
  const [form] = Form.useForm();
  const columnKeys = [
    'id',
    'tempId',
    'tempCode',
    'smsSignature',
    'tempContent',
    'addTime',
    'action',
  ];
  const [selectedColumns, setSelectedColumns] = useState(columnKeys);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  //Data Component
  const [smsTempData, setSMSTempData] = useState<SMSTempData[]>([]);
  const [selectedSMSTemp, setSelectedSMSTemp] = useState<SMSTempData>();
  const [SMSTempEnums, setSMSTempEnums] = useState<SMSTempEnum>();
  //Text Component
  const [fontSize, setFontSize] = useState<FontSizeType>(DEFAULT_FONT_SIZE);
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout>();
  const { t } = useTranslation();

  const columns: ColumnsType<SMSTempData> = [
    {
      title: setFont(t('settings.smsTempListColumns.id'), fontSize),
      key: 'id',
      dataIndex: 'id',
      fixed: screens.lg ? 'left' : undefined,
      width: 40,
      render: (text: string, record: SMSTempData) => (
        <Button
          type="link"
          disabled={!hasPermission(actionPermissions.pluginGroup.smsTemp)}
          onClick={() => {
            if (SMSTempEnums && SMSTempEnums.tempCodeList.length > 0) {
              setSelectedSMSTemp(record);
              setShowModal(true);
            }
          }}
          style={{ padding: 0, fontSize: fontSize }}
        >
          {text}
        </Button>
      ),
      sorter: (a: SMSTempData, b: SMSTempData) => compare(a.tempId, b.tempId),
    },
    {
      title: setFont(t('settings.smsTempListColumns.tempCode'), fontSize),
      key: 'tempCode',
      dataIndex: 'tempCode',
      width: 150,
      render: (text: string) => setFont(text, fontSize),
    },
    {
      title: setFont(t('settings.smsTempListColumns.tempId'), fontSize),
      key: 'tempId',
      dataIndex: 'tempId',
      width: 140,
      render: (text: string) => setFont(text, fontSize),
    },
    {
      title: setFont(t('settings.smsTempListColumns.smsSignature'), fontSize),
      key: 'smsSignature',
      dataIndex: 'smsSignature',
      width: 150,
      render: (text: string) => setFont(text, fontSize),
    },
    {
      title: setFont(t('settings.smsTempListColumns.tempContent'), fontSize),
      key: 'tempContent',
      dataIndex: 'tempContent',
      render: (text: string) => setFont(text, fontSize),
    },
    {
      title: setFont(t('settings.smsTempListColumns.addTime'), fontSize),
      key: 'addTime',
      dataIndex: 'addTime',
      width: 160,
      render: (text: string) => setFont(text, fontSize),
    },
    {
      title: t('actionsColumn.title'),
      width: 120,
      key: 'action',
      fixed: screens.lg ? 'right' : undefined,
      render: (record: SMSTempData) => (
        <Space size="small">
          <Button
            type="link"
            style={{ padding: 0, fontSize: fontSize }}
            onClick={() => {
              if (SMSTempEnums && SMSTempEnums.tempCodeList.length > 0) {
                setSelectedSMSTemp(record);
                setShowModal(true);
              }
            }}
            disabled={!hasPermission(actionPermissions.pluginGroup.smsTemp)}
          >
            {t('actionsColumn.edit')}
          </Button>

          {hasPermission(actionPermissions.pluginGroup.smsTemp) && (
            <Popconfirm
              title={setFont(t('actionsColumn.deleteWarning'), fontSize)}
              onConfirm={() => {
                deleteSMSTemp(record.id);
              }}
              okText={t('actionsColumn.confirmation.yes')}
              cancelText={t('actionsColumn.confirmation.no')}
              placement="leftBottom"
            >
              <Typography.Text type="danger" style={{ fontSize: fontSize }}>
                {t('actionsColumn.delete')}
              </Typography.Text>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  /**
   * @param id Id of SMSTemp to delete
   */
  const deleteSMSTemp = (id: number) => {
    if (isSubscribed.current) setIsLoading(true);
    postDataWithAuthToken('sms_temp/delete', { id: id })
      .then((response) => {
        if (response && response.goodStatus) {
          alertMessage('success', t('settings.alerts.smsTempDeleted'));
          getSMSTempData();
        } else {
          alertMessage(
            'error',
            response?.msg || t('general.noResponse'),
            response?.data || undefined
          );
        }
        if (isSubscribed.current) setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        if (isSubscribed.current) setIsLoading(false);
      });
  };

  //Get SMS Template data
  const getSMSTempData = useCallback(() => {
    if (isSubscribed.current) setIsLoading(true);
    getDataWithAuthToken('sms_temp/list', {
      params: {
        page: page,
        size: pageSize,
        keyword: keyword || undefined,
        tempCode:
          formRef.current && form.getFieldValue('tempCode')
            ? form.getFieldValue('tempCode') === 'all'
              ? undefined
              : form.getFieldValue('tempCode')
            : undefined,
      },
    })
      .then((response) => {
        if (response) {
          if (response.goodStatus) {
            if (isSubscribed.current) {
              setSMSTempData(response.data.list);
              setTotal(response.data.totalItem);
              setSelectedRowKeys([]);
              // Scroll to top when data changes
              tableScrollToTop();
            }
          } else if (response.returnCode === 403) {
            if (isSubscribed.current) setFourZeroThree(true);
          } else {
            isSubscribed.current && setFiveHundred(true);
            alertMessage(
              'error',
              response?.msg || t('general.noResponse'),
              response?.data || undefined
            );
          }
        } else isSubscribed.current && setFiveHundred(true);
        if (isSubscribed.current) setIsLoading(false);
      })
      .catch((err) => {
        if (isSubscribed.current) setIsLoading(false);
        console.log(err);
      });
  }, [t, form, keyword, page, pageSize]);

  // Get SMS Temp Enums
  const getSMSTempEnums = useCallback(() => {
    getDataWithAuthToken('sms_temp/enum_list')
      .then((response) => {
        if (response && response.goodStatus) {
          if (isSubscribed.current) setSMSTempEnums(response.data);
        } else {
          alertMessage(
            'error',
            response?.msg || t('general.noResponse'),
            response?.data || undefined
          );
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, [t]);

  // Sets isSubscribed to false if component becomes unmounted
  useEffect(() => {
    return () => {
      isSubscribed.current = false;
    };
  }, []);

  useEffect(() => {
    getSMSTempData();
  }, [getSMSTempData]);

  useEffect(() => {
    getSMSTempEnums();
  }, [getSMSTempEnums]);

  const advancedSearch = (
    <Popover
      overlayStyle={{ zIndex: 100 }}
      style={{ width: '200%' }}
      title={t('settings.smsTempListColumns.advancedSearch.title')}
      trigger="click"
      placement="bottomRight"
      content={
        <Form
          layout="vertical"
          form={form}
          ref={formRef}
          initialValues={{ tempCode: 'all' }}
        >
          <Form.Item
            label={t('settings.smsTempListColumns.advancedSearch.tempCode')}
            name="tempCode"
          >
            <Select
              placeholder={t('general.inputError.pleaseSelectOne')}
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              filterOption={false}
              onChange={(value) =>
                setAdvance((prev: any) => ({
                  ...prev,
                  tempCode: value === 'all' ? '' : value,
                }))
              }
            >
              <Select.Option key="all" value="all">
                {t('general.all')}
              </Select.Option>
              {SMSTempEnums?.tempCodeList.map((tempCode) => (
                <Select.Option key={tempCode.code} value={tempCode.code}>
                  {tempCode.code}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                htmlType="submit"
                type="primary"
                onClick={() => {
                  setKeyword('');
                  if (typingTimeout) clearTimeout(typingTimeout);
                  setTypingTimeout(
                    setTimeout(() => getSMSTempData(), GENERAL_TIMEOUT)
                  );
                }}
              >
                {t('settings.smsTempListColumns.advancedSearch.search')}
              </Button>
              <Button
                disabled={Object.values(advance).every((value) => !value)}
                onClick={() => {
                  form.resetFields();
                  setAdvance({});
                  getSMSTempData();
                }}
              >
                {t('settings.smsTempListColumns.advancedSearch.reset')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      }
    >
      <Badge
        count={Object.keys(advance).reduce((accumulator, obj) => {
          if (advance[obj as keyof typeof advance]) {
            return accumulator + 1;
          }

          return accumulator;
        }, 0)}
      >
        <Button icon={<FilterOutlined />}>
          {t('settings.smsTempListColumns.advancedSearch.title')}
        </Button>
      </Badge>
    </Popover>
  );

  return (
    <Container>
      {fourZeroThree ? (
        <Card>
          <FourZeroThree />
        </Card>
      ) : fiveHundred ? (
        <Card>
          <FiveHundred />
        </Card>
      ) : (
        <>
          <Card>
            <Typography.Title level={3} style={{ fontWeight: 500 }}>
              {t('settings.smsTemp')}
            </Typography.Title>

            <TableToolbar
              advancedSearch={advancedSearch}
              setFontSize={setFontSize}
              fontSize={fontSize}
              leftElement={
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setSelectedSMSTemp(undefined);
                    setShowModal(true);
                  }}
                  disabled={
                    !hasPermission(actionPermissions.pluginGroup.smsTemp)
                  }
                >
                  {t('settings.add/editSMSTemp.addTitle')}
                </Button>
              }
              tableSize={tableSize}
              setTableSize={setTableSize}
              refresh={() => {
                getSMSTempData();
              }}
              totalItems={total}
              columns={columns}
              columnKeys={columnKeys}
              selectedColumns={selectedColumns}
              setSelectedColumns={setSelectedColumns}
              search={(keyword) => {
                setKeyword(keyword);
                setPage(1);
              }}
              searchPlaceholder={t('searchPlaceholders.searchSMSTemp')}
              rows={smsTempData.map((smsTemp) => ({
                ...smsTemp,
              }))}
              exportConfig={{
                fileName: 'SMS_TEMPLATES',
              }}
            />
            <Table<SMSTempData>
              dataSource={smsTempData}
              rowKey={(articleCat) => articleCat.id}
              columns={columns.filter((x) =>
                selectedColumns.includes(x.key?.toString() ?? '')
              )}
              components={vt}
              scroll={{ y: 600, x: 1200 }}
              size={tableSize}
              loading={isLoading}
              pagination={{
                total: total,
                showQuickJumper: true,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  t('general.paginationTotal', {
                    start: range[0],
                    end: range[1],
                    total: total,
                  }),
                size: 'small',
                defaultPageSize: pageSize,
                onChange: (page, pSize) => {
                  setPage(page);
                  setPageSize(pSize || pageSize);
                  setSelectedRowKeys([]);
                },
                current: page,
              }}
            />
            {!!selectedRowKeys.length && (
              <TableFooterToolbar
                selectedRows={[]}
                columns={columns.filter((x) =>
                  selectedColumns.includes(x.key?.toString() ?? '')
                )}
                funct={{ exportConfig: { fileName: 'SMS_TEMPLATES' } }}
              />
            )}
          </Card>
          <SMSTemplateModal
            visible={showModal}
            setVisible={setShowModal}
            callBack={() => getSMSTempData()}
            smsTempEnums={SMSTempEnums}
            selectedSMSTemp={selectedSMSTemp}
          />
        </>
      )}
    </Container>
  );
};

export default SMSTemplateList;
